import React from "react";
import { AssignmentStats } from "../../../lib/api/dashboard";

interface AssignmentStatusChartProps {
  data?: AssignmentStats;
  loading?: boolean;
}

const AssignmentStatusChart: React.FC<AssignmentStatusChartProps> = ({
  data,
  loading,
}) => {
  const statuses = [
    {
      key: "pending",
      label: "Pending",
      color: "bg-amber-500",
      textColor: "text-amber-700",
      bgLight: "bg-amber-50",
    },
    {
      key: "approved",
      label: "Approved",
      color: "bg-blue-500",
      textColor: "text-blue-700",
      bgLight: "bg-blue-50",
    },
    {
      key: "active",
      label: "Active",
      color: "bg-emerald-500",
      textColor: "text-emerald-700",
      bgLight: "bg-emerald-50",
    },
    {
      key: "completed",
      label: "Completed",
      color: "bg-green-500",
      textColor: "text-green-700",
      bgLight: "bg-green-50",
    },
    {
      key: "rejected",
      label: "Rejected",
      color: "bg-red-500",
      textColor: "text-red-700",
      bgLight: "bg-red-50",
    },
    {
      key: "terminated",
      label: "Terminated",
      color: "bg-gray-500",
      textColor: "text-gray-700",
      bgLight: "bg-gray-50",
    },
  ];

  const total = data?.total || 1;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
        <div className="animate-pulse">
          <div className="h-5 w-40 bg-gray-200 rounded mb-4" />
          <div className="h-4 w-full bg-gray-200 rounded-full mb-6" />
          <div className="grid grid-cols-2 gap-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">Assignment Status</h3>
        <span className="text-sm text-gray-500">{data?.total ?? 0} total</span>
      </div>

      {/* Progress bar */}
      <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden flex mb-6">
        {statuses.map((status) => {
          const value =
            (data?.[status.key as keyof AssignmentStats] as number) || 0;
          const percent = (value / total) * 100;
          if (percent === 0) return null;
          return (
            <div
              key={status.key}
              className={`${status.color} transition-all duration-500`}
              style={{ width: `${percent}%` }}
              title={`${status.label}: ${value}`}
            />
          );
        })}
      </div>

      {/* Status grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {statuses.map((status) => {
          const value =
            (data?.[status.key as keyof AssignmentStats] as number) || 0;
          const percent = Math.round((value / total) * 100);
          return (
            <div
              key={status.key}
              className={`${status.bgLight} rounded-lg p-3 flex items-center gap-3`}
            >
              <div className={`w-2.5 h-2.5 rounded-full ${status.color}`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 truncate">{status.label}</p>
                <p className={`text-sm font-semibold ${status.textColor}`}>
                  {value}{" "}
                  <span className="text-xs font-normal text-gray-500">
                    ({percent}%)
                  </span>
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AssignmentStatusChart;
